import { Stack, Typography } from '@mui/material';
import Calendar, { ActivityInCalendar } from './Calendar';
import { tzdate, TZDate } from '@repo/tzdate';
import { DateRange } from '@mui/x-date-pickers-pro';
import { useAvailabilities } from '../hooks/availabilities';
import { BREAKPOINT_MD } from '../utils';
import { ProductInstance } from '@repo/types';
import { useMemo } from 'react';
import { GuestConfig } from 'src/hooks/domain/booking/useAccommodationSearchQuantities';

export function Availabilities(props: {
    availabilities: ReturnType<typeof useAvailabilities>['data'];
    availabilitiesIsLoading: boolean;
    provider: 'visbook' | 'bilberry';
    selectedDateRange: DateRange<TZDate>;
    setSelectedDateRange: (value: DateRange<TZDate>) => void;
    guests: GuestConfig[];
    setSearchDateRange: (value: DateRange<TZDate>) => void;
    minSearchDate?: TZDate;
}) {
    const {
        availabilities,
        availabilitiesIsLoading,
        provider,
        selectedDateRange,
        setSelectedDateRange,
        guests,
        setSearchDateRange,
    } = props;

    const activitiesInCalendar = useMemo(() => {
        if (!availabilities) return [];
        return availabilities.flat(1).map(mapInstanceToActivityInCalendar);
    }, [availabilities]);

    return (
        <Stack>
            <Typography variant="h3" m={0}>
                &nbsp;
            </Typography>
            <Typography
                pt={2}
                fontSize="calc(1rem * 0.85)"
                sx={{
                    display: 'none',
                    [`@container (width > ${BREAKPOINT_MD})`]: {
                        display: 'block',
                    },
                }}
            >
                &nbsp;
            </Typography>
            <Calendar
                isLoading={availabilitiesIsLoading}
                minDate={props.minSearchDate}
                activities={activitiesInCalendar}
                value={selectedDateRange}
                onChange={setSelectedDateRange}
                guests={guests}
                onChangeMonth={(value) => {
                    // Convert Dayjs to TZDate if needed
                    const tzValue = value instanceof TZDate ? value : tzdate(value);

                    // Set start of search-range to 1st day of chosen-month.
                    let start = tzValue.startOf('day');

                    // Set end of search-range to 1st day of month after
                    // chosen-month.
                    const end = start.endOf('month').add(1, 'day').startOf('day');

                    // If start of search-range is earlier than now(),
                    // set to now(), because you can't book something
                    // earlier than today.
                    // (The reason we set to start-of-today is because
                    // we want the same identical timestamp, for cache
                    // to work between month/toggling)
                    if (start.isBefore(TZDate.now(), 'day')) start = TZDate.now().startOf('day');

                    setSearchDateRange([start, end]);
                }}
                expectPrice={provider === 'bilberry'}
            />
        </Stack>
    );
}

export function mapInstanceToActivityInCalendar(instance: ProductInstance): ActivityInCalendar {
    return {
        start: instance.start,
        id: instance.id,
        capacity: instance.capacity,
        personsPerUnit: instance.personsPerUnit,
        firstTicketOptionPrice: instance.ticketOptions[0]?.price ?? null, // We are assuming that the first ticket option is the cheapest?
    };
}
